'use strict';

const { default: i18n } = require('i18next');
const parseUrl = require('url-parse');
const $ = require('jquery');
const utils = require('./utils');

const DEFAULT_LANGUAGE = 'en';
const LANGUAGES = [
  DEFAULT_LANGUAGE,
  'de',
  'es',
  'nl',
  'fr',
  'ja',
];

const COOKIE_LANGUAGE = `preferred-language-${NETOP_CONFIG.NETOP_DEPLOYMENT_ENV}`;

const LANGUAGE_SOURCE = {
  BROWSER: 'BROWSER',
  DEFAULT: 'DEFAULT',
  COOKIE:  'COOKIE',
  URL:     'URL',
  NATIVE:  'NATIVE', // from guest
  USER:    'USER',
};

const currentLanguage = {
  name:   DEFAULT_LANGUAGE,
  source: LANGUAGE_SOURCE.DEFAULT,
};

const enabled = true;

const has = lang => LANGUAGES.includes(lang);

let BROWSER_LANGUAGE = null;

// language defs may have up to three tags, separated by dash:
// language - 2 chars (en, fr ...)
// writing (optional) - 4 chars (ex Kana, Cyrl ...)
// region (optional) - 2 chars or 3 numbers (ex ES, 013 ...)
const getSupportedLanguage = (langTags) => {
  const tags = (langTags && langTags.toLowerCase()) || null;

  if (!tags) return null;

  // has all tags?
  if (has(tags)) return tags; // ex en, ja-Kana, ru-Cyrl-BY

  // get first 2 tags, the last one we don't need since
  // the full langTags was not supported
  const [langTag = null, tag2 = null] = tags.split('-');

  if (has([langTag, tag2].join('-'))) return [langTag, tag2].join('-'); // ex en-US, es-013, es-ES

  // get only first particle (en, de, fr ...) without variations
  if (has(langTag)) return langTag;

  return null;
};

// detect browser language
const getBrowserLanguage = () => {
  BROWSER_LANGUAGE = getSupportedLanguage(navigator.language, 'browser');
};

getBrowserLanguage();

const updateCss = () => {
  $(document.body)
    .removeClass(LANGUAGES.map(l => `lang-${l}`).join(' '))
    .addClass(`lang-${currentLanguage.name}`);
};

// set/remove lang cookie
// called from app startup, if lang was set from url
// and from UI dropdown
const setPreferred = (lang, source) => {
  if (!enabled) {
    return Promise.resolve(currentLanguage.name);
  }
  if (!has(lang)) {
    return Promise.resolve(currentLanguage.name);
  }
  return i18n.changeLanguage(lang)
    .then(() => {
      currentLanguage.name = lang;
      currentLanguage.source = source;
      const domain = utils.getTopDomain();
      const cookieVal = (source === LANGUAGE_SOURCE.BROWSER) ? '' : lang;
      document.cookie = `${COOKIE_LANGUAGE}=${cookieVal}; path=/; max-age=31536000; ;domain=${domain}`;
      updateCss();
      return lang;
    })
    .catch((error) => {
      console.log(error);
      return Promise.resolve(currentLanguage.name);
    });
};

// const onBrowserLanguageChange = () => {
//   getBrowserLanguage();

//   if (BROWSER_LANGUAGE && currentLanguage.source === LANGUAGE_SOURCE.BROWSER) {
//     setPreferred(BROWSER_LANGUAGE, LANGUAGE_SOURCE.BROWSER);
//   }
// };

const initLanguage = ({ url }) => {
  if (!enabled) {
    return DEFAULT_LANGUAGE;
  }

  // window.removeEventListener('languagechange', onBrowserLanguageChange);
  // window.addEventListener('languagechange', onBrowserLanguageChange);

  let preferredLanguage = null;

  // check url for language (from guest, mail etc)
  if (url) {
    const objUrl = parseUrl(url, true);
    if (objUrl.query) {
      // just for the guest
      const nativeLang = getSupportedLanguage(objUrl.query['native_lang'], 'native_lang');  // eslint-disable-line
      if (nativeLang) {
        currentLanguage.source = LANGUAGE_SOURCE.NATIVE;
        preferredLanguage = nativeLang;
      }
      // url's with lang param (ex a link from an email)
      const lang = getSupportedLanguage(objUrl.query['lang'], 'url');  // eslint-disable-line
      if (lang) {
        currentLanguage.source = LANGUAGE_SOURCE.URL;
        preferredLanguage = lang;
      }
    }
  }

  // if no lang specified in the url, check the cookie lang
  if (!preferredLanguage) {
    try {
      const cookieLang = document.cookie.split('; ') // eslint-disable-line
        .find(row => row.startsWith(COOKIE_LANGUAGE))
        .split('=')[1];
      const lang = getSupportedLanguage(cookieLang, 'cookie');
      if (lang) {
        currentLanguage.source = LANGUAGE_SOURCE.COOKIE;
        preferredLanguage = lang;
      }
    } catch (e) {} // eslint-disable-line
  }

  // browser language
  if (!preferredLanguage && BROWSER_LANGUAGE) {
    preferredLanguage = BROWSER_LANGUAGE;
    currentLanguage.source = LANGUAGE_SOURCE.BROWSER;
  }

  // default language
  if (!preferredLanguage) {
    preferredLanguage = DEFAULT_LANGUAGE;
    currentLanguage.source = LANGUAGE_SOURCE.DEFAULT;
  }

  // make it globally available
  currentLanguage.name = preferredLanguage;

  return preferredLanguage;
};

const initialize = ({ url = null } = {}) => {
  initLanguage({ url });
  updateCss();
};

const forExport = {
  enabled,
  initialize,
  setPreferred,
  updateCss,
  currentLanguage,
  LANGUAGES,
  BROWSER_LANGUAGE,
  DEFAULT_LANGUAGE,
  LANGUAGE_SOURCE,
};

module.exports = forExport;
